import { PropsWithChildren } from 'react';

import { Box, Typography } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';

import { usePrevious } from '@cast/utils';

import { tokens } from '../../../theme/generated/tokens';
import { usePreviewConfig } from '../_providers';

type FallbackProps = { resetError: () => void };

const Fallback = ({ resetError }: FallbackProps) => {
  const { renderFallback, data, errorMessage } = usePreviewConfig();
  const prev = usePrevious(data);

  if (prev && prev !== data) {
    resetError();
  }

  if (renderFallback) {
    return renderFallback(data, resetError);
  }

  return (
    <Box
      className="DsCode-error"
      bgcolor="inherit"
      p="8px"
      borderRadius={tokens.theme.radius.sm}
    >
      <Typography component="span" color={tokens.primitives.colors.red[400]}>
        ERROR:
      </Typography>
      <Typography
        component="span"
        pl="4px"
        color={tokens.primitives.colors.constants.white}
      >
        {errorMessage ?? 'Cannot display code preview'}
      </Typography>
    </Box>
  );
};

export const CodeErrorBoundary = ({ children }: PropsWithChildren<unknown>) => {
  return (
    <ErrorBoundary
      fallbackRender={({ resetErrorBoundary }) => (
        <Fallback resetError={resetErrorBoundary} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
};
